import { DECK } from "./engine.js";
import { scopedStorageKey } from "./runtime-context.js";

const MODEL_KEY = scopedStorageKey("personal-model-v1");
const FEATURE_COUNT = 11;
const DEFAULT_PREFERENCES = {
  directCall: true,
  aceDuck: {
    enabled: true,
    onlyFirstSuitRound: true,
    exactSuitLength: 2,
    blockCapotRisk: true,
    lateDefenseWithoutTrickFrom: 4,
  },
  tenForLast: {
    enabled: true,
    preserveWhenPossible: true,
    collectHeldPoints: true,
  },
  trumpForLast: {
    enabled: true,
    preserveWhenPossible: true,
  },
  forcedMoveDelayMs: 500,
  multipleChoiceDelayMs: 2500,
};

function sigmoid(value) {
  return 1 / (1 + Math.exp(-Math.max(-12, Math.min(12, value))));
}

function logit(probability) {
  const bounded = Math.max(0.01, Math.min(0.99, probability));
  return Math.log(bounded / (1 - bounded));
}

function cardFromId(id) {
  return DECK.find((card) => card.id === id);
}

export function featureVector(input, trump) {
  const hand = (input.hand || []).map((card) => (typeof card === "string" ? cardFromId(card) : card)).filter(Boolean);
  const center = typeof input.center === "string" ? cardFromId(input.center) : input.center;
  const known = center ? [...hand, center] : hand;
  const trumps = known.filter((card) => card.suit === trump);
  const outsideAces = known.filter((card) => card.suit !== trump && card.rank === "A").length;
  const has = (rank) => trumps.some((card) => card.rank === rank) ? 1 : 0;
  const belote = has("K") && has("Q") ? 1 : 0;
  const positions = ["1", "2", "3", "D"];

  return [
    1,
    center?.suit === trump ? 1 : 0,
    trumps.length / 6,
    has("J"),
    has("9"),
    has("A"),
    outsideAces / 3,
    belote,
    positions.indexOf(input.position) / 3,
    trumps.filter((card) => ["J", "9", "A", "10"].includes(card.rank)).length / 4,
    hand.filter((card) => card.suit === trump).length / 5,
  ];
}

export function emptyModel() {
  return {
    version: 1,
    weights: Array(FEATURE_COUNT).fill(0),
    outcomeSamples: 0,
    decisionSamples: 0,
    trainedAt: null,
    style: { pass: 0, take: 0, suits: { clubs: 0, diamonds: 0, hearts: 0, spades: 0 } },
    preferences: { ...DEFAULT_PREFERENCES },
  };
}

export function loadModel() {
  try {
    const stored = JSON.parse(localStorage.getItem(MODEL_KEY));
    if (stored?.version === 1 && stored.weights?.length === FEATURE_COUNT) {
      return {
        ...emptyModel(),
        ...stored,
        preferences: {
          ...DEFAULT_PREFERENCES,
          ...stored.preferences,
          aceDuck: {
            ...DEFAULT_PREFERENCES.aceDuck,
            ...(typeof stored.preferences?.aceDuck === "object" ? stored.preferences.aceDuck : {}),
          },
          tenForLast: {
            ...DEFAULT_PREFERENCES.tenForLast,
            ...(typeof stored.preferences?.tenForLast === "object" ? stored.preferences.tenForLast : {}),
          },
          trumpForLast: {
            ...DEFAULT_PREFERENCES.trumpForLast,
            ...(typeof stored.preferences?.trumpForLast === "object" ? stored.preferences.trumpForLast : {}),
          },
        },
      };
    }
  } catch {
    // A malformed local profile is replaced with a clean one.
  }
  return emptyModel();
}

export function saveModel(model) {
  localStorage.setItem(MODEL_KEY, JSON.stringify(model));
}

export function personalizedProbability(engineProbability, input, trump, model) {
  if (!model || model.outcomeSamples < 5) return null;
  const features = featureVector(input, trump);
  const residual = features.reduce((total, feature, index) => total + feature * model.weights[index], 0);
  const reliability = Math.min(1, model.outcomeSamples / 30);
  return sigmoid(logit(engineProbability / 100) + residual * reliability) * 100;
}

export function decorateAnalysis(analysis, input, model) {
  if (!analysis?.bySuit) return analysis;
  for (const [suit, result] of Object.entries(analysis.bySuit)) {
    result.personalizedProbability = personalizedProbability(result.probability, input, suit, model);
  }
  return analysis;
}

export function trainModel(records) {
  const model = emptyModel();
  const completed = records.filter((record) =>
    record.input &&
    record.analysis?.bySuit &&
    record.decision?.startsWith("take:") &&
    ["win", "loss"].includes(record.outcome),
  );

  for (const record of records) {
    if (record.decision === "pass") model.style.pass += 1;
    if (record.decision?.startsWith("take:")) {
      const suit = record.decision.slice(5);
      model.style.take += 1;
      if (suit in model.style.suits) model.style.suits[suit] += 1;
    }
  }
  model.decisionSamples = model.style.pass + model.style.take;

  for (let epoch = 0; epoch < 18; epoch += 1) {
    completed.forEach((record, sampleIndex) => {
      const trump = record.decision.slice(5);
      const engineProbability = record.analysis.bySuit[trump]?.probability;
      if (!Number.isFinite(engineProbability)) return;
      const features = featureVector(record.input, trump);
      const adjustment = features.reduce((total, feature, index) => total + feature * model.weights[index], 0);
      const predicted = sigmoid(logit(engineProbability / 100) + adjustment);
      const target = record.outcome === "win" ? 1 : 0;
      const learningRate = 0.055 / Math.sqrt(1 + sampleIndex * 0.08 + epoch * 0.2);
      features.forEach((feature, index) => {
        const regularization = index === 0 ? 0 : model.weights[index] * 0.002;
        model.weights[index] += learningRate * ((target - predicted) * feature - regularization);
        model.weights[index] = Math.max(-1.8, Math.min(1.8, model.weights[index]));
      });
    });
  }

  model.outcomeSamples = completed.length;
  model.trainedAt = new Date().toISOString();
  return model;
}
